import { MGPOptional, MGPValidation } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { Player } from '../../jscaip/Player';
import { RelativePlayer } from '../../jscaip/RelativePlayer';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { Debug } from '../../utils/Debug';
import { TaflFailure } from './TaflFailure';
import { TaflPawn } from './TaflPawn';
import { TaflState } from './TaflState';
export class TaflNode extends GameNode {
}
export class TaflRules extends ConfigurableRules {
    generateMove;
    static CAN_RETURN_TO_CASTLE = () => $localize `Central throne is left for good`;
    static EDGE_ARE_KING_S_ENNEMY = () => $localize `Edges are king's ennemy`;
    static CENTRAL_THRONE_CAN_SURROUND_KING = () => $localize `Central throne can surround king`;
    static KING_FAR_FROM_HOME_CAN_BE_SANDWICHED = () => $localize `King far from home can be sandwiched`;
    static INVADER_STARTS = () => $localize `Invader starts`;
    constructor(generateMove) {
        super();
        this.generateMove = generateMove;
    }
    isLegal(move, state, config) {
        const player = state.getCurrentPlayer();
        const validity = this.getMoveValidity(player, move, state, config);
        if (validity.isFailure()) {
            return MGPValidation.failure(validity.getReason());
        }
        return MGPValidation.SUCCESS;
    }
    getMoveValidity(player, move, state, config) {
        const owner = state.getRelativeOwner(player, move.getStart());
        if (owner === RelativePlayer.NONE) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (owner === RelativePlayer.OPPONENT) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        const landingCoordOwner = state.getRelativeOwner(player, move.getEnd());
        if (landingCoordOwner !== RelativePlayer.NONE) {
            return MGPValidation.failure(TaflFailure.LANDING_ON_OCCUPIED_SQUARE());
        }
        if (this.isThrone(state, move.getEnd())) {
            if (state.getPieceAt(move.getStart()).isKing()) {
                if (state.isCentralThrone(move.getEnd()) && config.canReturnToCastle === false) {
                    return MGPValidation.failure(TaflFailure.THRONE_IS_LEFT_FOR_GOOD());
                }
            }
            else {
                return MGPValidation.failure(TaflFailure.SOLDIERS_CANNOT_SIT_ON_THRONE());
            }
        }
        const dir = move.getStart().getDirectionToward(move.getEnd()).get();
        const dist = move.getStart().getOrthogonalDistance(move.getEnd());
        let inspectedCoord = move.getStart().getNext(dir);
        for (let i = 1; i < dist; i++) {
            if (state.getPieceAt(inspectedCoord) !== TaflPawn.UNOCCUPIED) {
                return MGPValidation.failure(RulesFailure.SOMETHING_IN_THE_WAY());
            }
            inspectedCoord = inspectedCoord.getNext(dir);
        }
        return MGPValidation.SUCCESS;
    }
    isThrone(state, coord) {
        if (state.isExternalThrone(coord)) {
            return true;
        }
        else {
            return state.isCentralThrone(coord);
        }
    }
    tryCapture(player, landingPawn, d, state, config) {
        /* landingPawn is the piece that just moved
         * d the direction in witch we look for capture
         * return the captured coord, or null if no capture possible
         * 1. the threatened square doesn't exist      -> no capture
         * 2: the threatened square is not an opponent -> no capture
         * 3: the threatened square is a king          -> delegate calculation
         * 4: the threatened square is a piece         -> delegate calculation
         */
        const threatened = landingPawn.getNext(d);
        if (state.isNotOnBoard(threatened)) {
            return MGPOptional.empty(); // 1: the threatened square dont exist, no capture
        }
        const threatenedPawnOwner = state.getRelativeOwner(player, threatened);
        if (threatenedPawnOwner !== RelativePlayer.OPPONENT) {
            return MGPOptional.empty(); // 2: the threatened square is not an opponent
        }
        if (state.getPieceAt(threatened).isKing()) {
            return this.captureKing(player, landingPawn, d, state, config);
        }
        return this.capturePawn(player, landingPawn, d, state);
    }
    captureKing(player, landingPiece, d, state, config) {
        const kingCoord = landingPiece.getNext(d);
        const { backCoord, back, backInRange, left, leftCoord, right, rightCoord, } = this.getSurroundings(kingCoord, d, player, state);
        if (backInRange === false) {
            return this.captureKingAgainstTheWall(left, right, kingCoord, config);
        }
        if (back === RelativePlayer.NONE && this.isThrone(state, backCoord)) {
            return this.captureKingAgainstThrone(state, backCoord, kingCoord, left, right, config);
        }
        if (back === RelativePlayer.PLAYER) {
            return this.captureKingWithAtLeastASandwich(state, kingCoord, left, leftCoord, right, rightCoord, config);
        }
        return MGPOptional.empty();
    }
    getSurroundings(c, d, player, state) {
        // the piece that just moved came from the front direction (by definition)
        const backCoord = c.getNext(d);
        const backInRange = state.isOnBoard(backCoord);
        const back = backInRange ?
            state.getRelativeOwner(player, backCoord) :
            RelativePlayer.NONE;
        const leftCoord = c.getLeft(d);
        const leftInRange = state.isOnBoard(leftCoord);
        const left = leftInRange ?
            state.getRelativeOwner(player, leftCoord) :
            RelativePlayer.NONE;
        const rightCoord = c.getRight(d);
        const rightInRange = state.isOnBoard(rightCoord);
        const right = rightInRange ?
            state.getRelativeOwner(player, rightCoord) :
            RelativePlayer.NONE;
        return {
            backCoord, back, backInRange,
            leftCoord, left,
            rightCoord, right,
        };
    }
    captureKingAgainstTheWall(left, right, kingCoord, config) {
        let nbInvaders = (left === RelativePlayer.PLAYER ? 1 : 0);
        nbInvaders += (right === RelativePlayer.PLAYER ? 1 : 0);
        if (nbInvaders === 2 && config.edgesAreKingsEnnemy) { // 2
            // king captured by 3 invaders against 1 border
            return MGPOptional.of(kingCoord);
        }
        // those were the only two way to capture against the border
        return MGPOptional.empty();
    }
    captureKingAgainstThrone(state, backCoord, kingCoord, left, right, config) {
        if (state.isExternalThrone(backCoord)) {
            if (config.kingFarFromHomeCanBeSandwiched) {
                return MGPOptional.of(kingCoord);
            }
        }
        else { // Central throne
            const kingHasOpponentOnItsLeft = left === RelativePlayer.PLAYER;
            const kingHasOpponentOnItsRight = right === RelativePlayer.PLAYER;
            const kingHasThreeOpponentAround = kingHasOpponentOnItsLeft || kingHasOpponentOnItsRight;
            if (config.centralThroneCanSurroundKing && kingHasThreeOpponentAround) {
                return MGPOptional.of(kingCoord);
            }
        }
        return MGPOptional.empty();
    }
    capturePawn(player, coord, direction, state) {
        /**
         * the captured piece is on the next coord after 'coord' (in direction 'direction')
         * coord partipate in the capture
         *
         * So these are the different capture ways :
         * - 2 opponents
         * - 1 opponents 1 empty-throne
         */
        const threatenedPieceCoord = coord.getNext(direction);
        const backCoord = threatenedPieceCoord.getNext(direction);
        // the piece that just move is always considered in front
        let back = RelativePlayer.NONE;
        if (state.isOnBoard(backCoord)) {
            back = state.getRelativeOwner(player, backCoord);
        }
        if (back === RelativePlayer.NONE) {
            if (this.isThrone(state, backCoord) === false) {
                Debug.display('TaflRules', 'capturePawn', 'cannot capture a piece without an ally; ' +
                    threatenedPieceCoord + 'threatened by ' + player + `'s piece in ` + coord +
                    ' coming from this direction (' + direction.x + ', ' + direction.y + ')' +
                    'cannot capture a piece without an ally behind');
                return MGPOptional.empty();
            } // here, back is an empty throne
            Debug.display('TaflRules', 'capturePawn', 'piece captured by 1 opponent and 1 throne; ' +
                threatenedPieceCoord + 'threatened by ' + player + `'s piece in ` + coord +
                ' coming from this direction (' + direction.x + ', ' + direction.y + ')');
            return MGPOptional.of(threatenedPieceCoord); // piece captured by 1 opponent and 1 throne
        }
        if (back === RelativePlayer.PLAYER) {
            Debug.display('TaflRules', 'capturePawn', 'piece captured by 2 opponents; ' + threatenedPieceCoord +
                'threatened by ' + player + `'s piece in ` + coord +
                ' coming from this direction (' + direction.x + ', ' + direction.y + ')');
            return MGPOptional.of(threatenedPieceCoord); // piece captured by two opponents
        }
        Debug.display('TaflRules', 'capturePawn', 'no captures; ' + threatenedPieceCoord + 'threatened by ' + player + `'s piece in ` + coord +
            ' coming from this direction (' + direction.x + ', ' + direction.y + ')');
        return MGPOptional.empty();
    }
    captureKingWithAtLeastASandwich(state, kingCoord, left, leftCoord, right, rightCoord, config) {
        if (this.kingTouchCentralThrone(state, kingCoord) === false &&
            config.kingFarFromHomeCanBeSandwiched) {
            return MGPOptional.of(kingCoord);
        }
        const throneCanSurrond = config.centralThroneCanSurroundKing;
        const leftIsThrone = this.isThrone(state, leftCoord);
        const leftCanSurround = left === RelativePlayer.PLAYER || (leftIsThrone && throneCanSurrond);
        const rightIsThrone = this.isThrone(state, rightCoord);
        const rightCanSurround = right === RelativePlayer.PLAYER || (rightIsThrone && throneCanSurrond);
        if (leftCanSurround && rightCanSurround) {
            return MGPOptional.of(kingCoord); // king captured by 4 invaders
        }
        return MGPOptional.empty();
    }
    kingTouchCentralThrone(state, kingCoord) {
        const centralThrone = state.getCentralThrone();
        return kingCoord.getOrthogonalDistance(centralThrone) <= 1;
    }
    applyLegalMove(move, state, config, _info) {
        const turn = state.turn;
        const board = state.getCopiedBoard();
        const player = state.getCurrentPlayer();
        const start = move.getStart();
        const end = move.getEnd();
        board[end.y][end.x] = board[start.y][start.x]; // move the piece to the new position
        board[start.y][start.x] = TaflPawn.UNOCCUPIED; // remove it from the previous position
        for (const d of Orthogonal.ORTHOGONALS) {
            const captured = this.tryCapture(player, move.getEnd(), d, state, config);
            if (captured.isPresent()) {
                board[captured.get().y][captured.get().x] = TaflPawn.UNOCCUPIED;
            }
        }
        return new TaflState(board, turn + 1);
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const winner = this.getWinner(state, config);
        if (winner.isPresent()) {
            return GameStatus.getVictory(winner.get());
        }
        return GameStatus.ONGOING;
    }
    getWinner(state, config) {
        const optionalKingCoord = this.getKingCoord(state);
        if (optionalKingCoord.isAbsent()) {
            Debug.display('TaflRules', 'getWinner', 'The king is dead, victory to invader');
            // the king is dead, long live the king
            return MGPOptional.of(this.getInvader(config));
        }
        const kingCoord = optionalKingCoord.get();
        if (state.isExternalThrone(kingCoord)) {
            Debug.display('TaflRules', 'getWinner', 'The king escape, victory to defender');
            // king reached one corner!
            return MGPOptional.of(this.getDefender(config));
        }
        if (this.isPlayerImmobilized(Player.ZERO, state, config)) {
            Debug.display('TaflRules', 'getWinner', 'Zero has no move, victory to one');
            return MGPOptional.of(Player.ONE);
        }
        if (this.isPlayerImmobilized(Player.ONE, state, config)) {
            Debug.display('TaflRules', 'getWinner', 'One has no move, victory to zero');
            return MGPOptional.of(Player.ZERO);
        }
        Debug.display('TaflRules', 'getWinner', 'no victory');
        return MGPOptional.empty();
    }
    getKingCoord(state) {
        const size = state.getSize();
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                if (state.getPieceAtXY(x, y).isKing()) {
                    return MGPOptional.of(new Coord(x, y));
                }
            }
        }
        return MGPOptional.empty();
    }
    getInvader(config) {
        return config.invaderStarts ? Player.ZERO : Player.ONE;
    }
    getDefender(config) {
        return this.getInvader(config).getOpponent();
    }
    isPlayerImmobilized(player, state, config) {
        return this.getPlayerListMoves(player, state, config).length === 0;
    }
    getPlayerListMoves(player, state, config) {
        const listMoves = [];
        const listPawns = this.getPlayerListPawns(player, state);
        for (const piece of listPawns) {
            const pawnDestinations = this.getPossibleDestinations(piece, state, config);
            for (const destination of pawnDestinations) {
                const newMove = this.generateMove(piece, destination).get();
                listMoves.push(newMove);
            }
        }
        return listMoves;
    }
    getPlayerListPawns(player, state) {
        const size = state.getSize();
        const listPawn = [];
        for (let y = 0; y < size; y++) {
            for (let x = 0; x < size; x++) {
                // for each square
                const piece = new Coord(x, y);
                const owner = state.getAbsoluteOwner(piece);
                if (owner === player) {
                    listPawn.push(piece);
                }
            }
        }
        return listPawn;
    }
    getPossibleDestinations(start, state, config) {
        // search the possible destinations for the piece at "start"
        const destinations = [];
        let foundDestination;
        const pieceIsKing = state.getPieceAt(start).isKing();
        for (const dir of Orthogonal.ORTHOGONALS) {
            // we look for empty existing destinations in each direction as far as we can
            foundDestination = start.getNext(dir, 1);
            while (state.hasPieceAt(foundDestination, TaflPawn.UNOCCUPIED)) {
                if (state.isExternalThrone(foundDestination)) {
                    if (pieceIsKing) {
                        destinations.push(foundDestination);
                    }
                }
                else if (state.isCentralThrone(foundDestination)) {
                    if (pieceIsKing && config.canReturnToCastle) {
                        destinations.push(foundDestination);
                    }
                }
                else {
                    destinations.push(foundDestination);
                }
                foundDestination = foundDestination.getNext(dir, 1);
            }
        }
        return destinations;
    }
}
